// @systeme: interface
// Une liste : on ajoute en tapant, on coche d'un doigt, on vide le panier. Le mode
// courses grossit tout et garde l'écran allumé. Ce qu'on rachète souvent est proposé.
// Un lien secret partage la liste avec quelqu'un qui n'a pas de compte.
import { useCallback, useEffect, useRef, useState, type MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { ChevronDown, ChevronLeft, ChevronRight, EllipsisVertical, Eye } from 'lucide-react';
import { Link as LienRouteur, useNavigate, useParams } from 'react-router';
import type { Article, Liste, Rayon, Suggestion } from '@foyer/shared';
import { Chargement } from '../composants/Chargement';
import { useMessages } from '../composants/Messages';
import { api } from '../lib/api';
import { fonctionActive } from '../lib/fonctionnalites';
import { useSessionConnectee } from '../session/SessionContext';
import { nomRayon } from '../lib/rayons';
import { couleurs, tailles } from '../theme/theme';

/** Garde l'écran allumé pendant les courses, si le téléphone le permet. Silencieux sinon. */
function useEcranAllume(actif: boolean) {
  const verrou = useRef<WakeLockSentinel | undefined>(undefined);
  useEffect(() => {
    if (!actif || typeof navigator === 'undefined' || !navigator.wakeLock) return;
    let annule = false;
    navigator.wakeLock
      .request('screen')
      .then((v) => {
        if (annule) void v.release();
        else verrou.current = v;
      })
      .catch(() => undefined);
    return () => {
      annule = true;
      void verrou.current?.release();
      verrou.current = undefined;
    };
  }, [actif]);
}

/** Les articles à faire, groupés par rayon dans l'ordre reçu du serveur. */
function grouperParRayon(articles: Article[]): { rayon: Rayon | null; articles: Article[] }[] {
  const groupes: { rayon: Rayon | null; articles: Article[] }[] = [];
  for (const a of articles) {
    const dernier = groupes[groupes.length - 1];
    if (dernier && dernier.rayon === a.rayon) dernier.articles.push(a);
    else groupes.push({ rayon: a.rayon, articles: [a] });
  }
  return groupes;
}

export function ListeDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const session = useSessionConnectee();
  const { dire, direErreur } = useMessages();
  const [liste, setListe] = useState<Liste | undefined>();
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [saisie, setSaisie] = useState('');
  const [modeCourses, setModeCourses] = useState(false);
  const [voirPanier, setVoirPanier] = useState(false);
  const [menu, setMenu] = useState<{ ancre: HTMLElement; article: Article } | undefined>();
  const [renommage, setRenommage] = useState<string | undefined>();
  useEcranAllume(modeCourses);

  const charger = useCallback(async () => {
    try {
      const l = await api.listes.lire(id);
      setListe(l);
      setSuggestions(await api.listes.suggestions(id));
    } catch (e) {
      direErreur(e);
    }
  }, [id, direErreur]);

  useEffect(() => {
    api.listes.lire(id).then(setListe).catch(direErreur);
    api.listes
      .suggestions(id)
      .then(setSuggestions)
      .catch(() => undefined);
  }, [id, direErreur]);

  async function agir(action: () => Promise<unknown>, confirmation?: string) {
    try {
      await action();
      if (confirmation) dire(confirmation);
      await charger();
    } catch (e) {
      direErreur(e);
    }
  }

  async function ajouter(libelle: string) {
    if (libelle.trim() === '') return;
    setSaisie('');
    await agir(() => api.listes.ajouter(id, { libelle: libelle.trim() }));
  }

  if (!liste) return <Chargement />;
  const aFaire = liste.articles.filter((a) => !a.coche);
  const panier = liste.articles.filter((a) => a.coche);
  const taille = modeCourses ? '1.35rem' : undefined;
  const lienPartage = liste.jetonPartage
    ? `${window.location.origin}/liste/${liste.jetonPartage}`
    : undefined;

  async function copierLien() {
    if (!lienPartage) return;
    try {
      await navigator.clipboard.writeText(lienPartage);
      dire('Lien copié. Envoie-le à qui tu veux : pas besoin de compte pour cocher.');
    } catch {
      dire('Sélectionne le lien et copie-le à la main.');
    }
  }

  return (
    <Box sx={{ px: `${tailles.marge}px`, pt: 3, pb: 4 }}>
      <Button
        component={LienRouteur}
        to="/listes"
        startIcon={<ChevronLeft size={18} />}
        color="inherit"
        sx={{ px: 0, mb: 1 }}
      >
        Listes
      </Button>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
        {renommage !== undefined ? (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              void agir(() => api.listes.renommer(id, renommage.trim()), 'C’est noté.').then(() =>
                setRenommage(undefined),
              );
            }}
            sx={{ flex: 1 }}
          >
            <TextField
              value={renommage}
              onChange={(e) => setRenommage(e.target.value)}
              autoFocus
              inputProps={{ maxLength: 80, 'aria-label': 'Nom de la liste' }}
            />
          </Box>
        ) : (
          <Typography
            variant="h1"
            component="h1"
            onClick={liste.type === 'autre' ? () => setRenommage(liste.nom) : undefined}
          >
            {liste.nom}
          </Typography>
        )}
        <Button
          variant={modeCourses ? 'contained' : 'outlined'}
          startIcon={<Eye size={18} />}
          onClick={() => setModeCourses((v) => !v)}
          aria-pressed={modeCourses}
          sx={{ whiteSpace: 'nowrap' }}
        >
          {modeCourses ? 'Mode courses' : 'En magasin'}
        </Button>
      </Stack>
      {modeCourses && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Gros caractères, écran allumé. Coche au fur et à mesure.
        </Typography>
      )}

      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          void ajouter(saisie);
        }}
        sx={{ mb: 2 }}
      >
        <TextField
          value={saisie}
          onChange={(e) => setSaisie(e.target.value)}
          placeholder="Du lait, 3 baguettes, couches taille 4…"
          inputProps={{ maxLength: 120, 'aria-label': 'Ajouter', enterKeyHint: 'done' }}
        />
      </Box>

      {suggestions.length > 0 && !modeCourses && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Tu prends souvent :
          </Typography>
          <Stack direction="row" flexWrap="wrap" useFlexGap spacing={1}>
            {suggestions.map((s) => (
              <Chip
                key={s.libelle}
                label={s.libelle}
                variant="outlined"
                onClick={() => void ajouter(s.libelle)}
              />
            ))}
          </Stack>
        </Box>
      )}

      {aFaire.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
          {panier.length > 0 ? 'Tout est dans le panier.' : 'Rien à prendre pour le moment.'}
        </Typography>
      ) : (
        grouperParRayon(aFaire).map((g) => (
          <Box key={g.rayon ?? 'aucun'} sx={{ mb: 1.5 }}>
            {liste.type === 'courses' && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, pl: 0.5 }}>
                {nomRayon(g.rayon)}
              </Typography>
            )}
            <List disablePadding aria-label={nomRayon(g.rayon)}>
              {g.articles.map((a) => (
                <ListItem
                  key={a.id}
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label={`Options pour ${a.libelle}`}
                      onClick={(e: MouseEvent<HTMLElement>) =>
                        setMenu({ ancre: e.currentTarget, article: a })
                      }
                    >
                      <EllipsisVertical size={22} />
                    </IconButton>
                  }
                  sx={{
                    bgcolor: couleurs.blanc,
                    borderRadius: `${tailles.rayon}px`,
                    mb: 1,
                    border: 1,
                    borderColor: 'divider',
                  }}
                >
                  <ListItemButton
                    onClick={() =>
                      void agir(() => api.listes.modifierArticle(id, a.id, { coche: true }))
                    }
                    sx={{ minHeight: modeCourses ? 64 : tailles.cible + 8, pl: 0.5 }}
                  >
                    <Checkbox
                      checked={false}
                      tabIndex={-1}
                      inputProps={{ 'aria-label': `Pris : ${a.libelle}` }}
                      sx={{ '& .MuiSvgIcon-root': { fontSize: modeCourses ? 34 : 28 } }}
                    />
                    <ListItemText
                      primary={a.quantite ? `${a.libelle} · ${a.quantite}` : a.libelle}
                      secondary={a.recurrent ? 'à chaque fois' : undefined}
                      primaryTypographyProps={{ sx: { fontSize: taille } }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        ))
      )}

      {panier.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Button
              onClick={() => setVoirPanier((v) => !v)}
              color="inherit"
              startIcon={voirPanier ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
              sx={{ px: 0 }}
            >
              Dans le panier ({panier.length})
            </Button>
            <Button
              size="small"
              onClick={() => void agir(() => api.listes.vider(id), 'Panier vidé. À la prochaine !')}
            >
              Vider le panier
            </Button>
          </Stack>
          <Collapse in={voirPanier}>
            <List disablePadding aria-label="Dans le panier">
              {panier.map((a) => (
                <ListItem key={a.id} disablePadding>
                  <ListItemButton
                    onClick={() =>
                      void agir(() => api.listes.modifierArticle(id, a.id, { coche: false }))
                    }
                    sx={{ pl: 0.5 }}
                  >
                    <Checkbox
                      checked
                      tabIndex={-1}
                      inputProps={{ 'aria-label': `Remettre : ${a.libelle}` }}
                    />
                    <ListItemText
                      primary={a.libelle}
                      primaryTypographyProps={{
                        sx: { textDecoration: 'line-through', color: 'text.secondary' },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      )}

      <Menu anchorEl={menu?.ancre} open={menu !== undefined} onClose={() => setMenu(undefined)}>
        {menu && (
          <MenuItem
            onClick={() => {
              const a = menu.article;
              setMenu(undefined);
              void agir(
                () => api.listes.modifierArticle(id, a.id, { recurrent: !a.recurrent }),
                a.recurrent
                  ? 'D’accord, plus à chaque fois.'
                  : 'Noté : il reviendra à chaque tournée.',
              );
            }}
          >
            {menu.article.recurrent ? 'Plus à chaque fois' : 'À chaque fois'}
          </MenuItem>
        )}
        {menu && (
          <MenuItem
            onClick={() => {
              const a = menu.article;
              setMenu(undefined);
              const q = window.prompt('Quelle quantité ?', a.quantite ?? '');
              if (q === null) return;
              void agir(() =>
                api.listes.modifierArticle(id, a.id, {
                  quantite: q.trim() === '' ? null : q.trim(),
                }),
              );
            }}
          >
            Quantité
          </MenuItem>
        )}
        {menu && (
          <MenuItem
            onClick={() => {
              const a = menu.article;
              setMenu(undefined);
              void agir(() => api.listes.retirer(id, a.id), 'Retiré.');
            }}
            sx={{ color: 'error.main' }}
          >
            Retirer
          </MenuItem>
        )}
      </Menu>

      {!modeCourses && fonctionActive(session, 'partage_listes') && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h3" component="h2" sx={{ mb: 1 }}>
            Partager
          </Typography>
          {lienPartage ? (
            <Stack spacing={1}>
              <Typography variant="body2" color="text.secondary">
                Qui a ce lien voit la liste et coche, sans compte. Rien d’autre du foyer n’est
                visible.
              </Typography>
              <TextField
                value={lienPartage}
                InputProps={{ readOnly: true }}
                inputProps={{ 'aria-label': 'Lien de partage' }}
                onFocus={(e) => e.target.select()}
              />
              <Stack direction="row" spacing={1}>
                <Button variant="outlined" onClick={() => void copierLien()}>
                  Copier le lien
                </Button>
                <Button
                  color="inherit"
                  onClick={() =>
                    void agir(() => api.listes.arreterPartage(id), 'Le lien ne marche plus.')
                  }
                >
                  Arrêter le partage
                </Button>
              </Stack>
            </Stack>
          ) : (
            <Button
              variant="outlined"
              onClick={() =>
                void agir(() => api.listes.partager(id), 'Le lien est prêt, à copier ci-dessous.')
              }
            >
              Partager par lien
            </Button>
          )}
          {liste.type === 'autre' && (
            <Box sx={{ mt: 3 }}>
              <Button
                color="inherit"
                onClick={() =>
                  void agir(() => api.listes.supprimer(id), 'Liste supprimée.').then(
                    () => void navigate('/listes'),
                  )
                }
              >
                Supprimer cette liste
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}
